/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Paths for the rubrics API, as the studio module calls it.
 *
 * The tenant travels in the berlin-tenant-id header and `graphId` is a query param rather than a
 * path segment, because the API also serves SDK tenants that own rubrics directly. The studio is
 * always editing a graph, so `graphId` is required here — an SDK tenant never reaches this module,
 * and a path built without a graph would silently address the wrong scope.
 *
 * Deliberately a separate copy from the portal's builder rather than a shared import: the two
 * modules stay decoupled, and this one carries none of the portal's SDK branching.
 */

const RUBRICS_PATH = '/rubrics';

const withGraphScope = (path, params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      query.set(key, String(value));
    }
  });
  const search = query.toString();
  return search ? `${path}?${search}` : path;
};

const rubricPath = (rubricId, suffix = '') => `${RUBRICS_PATH}/${encodeURIComponent(String(rubricId))}${suffix}`;

export const buildRubricsListPath = (graphId) => withGraphScope(RUBRICS_PATH, { graphId });

export const buildRubricsCreatePath = (graphId) => withGraphScope(RUBRICS_PATH, { graphId });

export const buildRubricsDetailPath = (graphId, rubricId) => withGraphScope(rubricPath(rubricId), { graphId });

export const buildRubricsDeletePath = (graphId, rubricId) => withGraphScope(rubricPath(rubricId), { graphId });

export const buildRubricsTextPath = (graphId, rubricId) =>
  withGraphScope(rubricPath(rubricId, '/rubrictext'), { graphId });

export const buildRubricsSchedulerPath = (graphId, rubricId) =>
  withGraphScope(rubricPath(rubricId, '/scheduler'), { graphId });

/**
 * One enhance endpoint covers both cases: before the first save there is no rubricId, and the
 * param is simply omitted rather than the path changing shape.
 */
export const buildRubricsEnhancePath = (graphId, rubricId) =>
  withGraphScope(`${RUBRICS_PATH}/enhance`, { graphId, rubricId });
